import { inr } from './design/atoms';
import type { MemberGym } from './hooks/useMemberGyms';

/* Paying for a drop-in booking — the web's half of the contract the mobile app
   keeps in lib/coverage.ts + lib/bookingPay.ts. The rules must stay identical:

   - Money is taken online only where the gym collects for bookings
     (`features.bookingPayments`: a gateway AND the gym's opt-in). Everywhere
     else a paid booking is paid at the desk, and no copy may say otherwise.
     Until the desk records that money the booking carries `amountDue` — it is
     owing, never shown as paid.
   - Where it collects, the server books first and returns `pay`; the seat is
     the member's while they pay, until `pay.expiresAt`. A member who leaves the
     pay page pays later from My bookings (`POST /member/bookings/:id/pay`).
   - `allowUnpaidBooking: false` means "pay before you have it": met by paying
     online, otherwise the desk takes the booking. Absent reads as allowed. */

/** What the server attaches to a booking response when it wants paying for. */
export interface BookingPay {
  url: string;
  amount: number;
  /** When the seat is released if nobody pays. */
  expiresAt: string;
  purchaseId: string;
}

export function gymCollectsBookings(gym: MemberGym | null | undefined): boolean {
  return gym?.features?.bookingPayments === true && gym?.features?.payments === true;
}

/** May this paid drop-in be booked here at all? Paying online satisfies the rule. */
export function canBookUnpaid(allowUnpaidBooking: boolean | undefined, gym: MemberGym | null | undefined): boolean {
  return allowUnpaidBooking !== false || gymCollectsBookings(gym);
}

/** The book button on a paid drop-in. */
export function paidCta(amount: number, gym: MemberGym | null | undefined): string {
  return gymCollectsBookings(gym) ? `Book · pay ${inr(amount)}` : `Book · pay ${inr(amount)} at the gym`;
}

/**
 * The payment line on a confirmation — never claims a payment nobody took. A
 * collecting gym only shows a confirmation for a paid booking when its pay link
 * could not be made; the booking then still owes, payable from My bookings.
 */
export function paidChargeLine(amount: number, gym: MemberGym | null | undefined): string {
  return gymCollectsBookings(gym) ? `${inr(amount)} · pay from My bookings` : `${inr(amount)} · pay at the gym`;
}

/** Why the desk has to take this one. */
export function deskReason(gym: MemberGym | null | undefined): string {
  const who = gym?.displayName || gym?.name || 'This gym';
  return gym?.features?.payments
    ? 'Bookings that need paying for are completed at the front desk.'
    : `${who} doesn't take payments online yet.`;
}

export function frontDeskNumber(gym: MemberGym | null | undefined): string | null {
  return gym?.phoneNumbers?.find(n => !!n && n.trim().length > 0)?.trim() ?? null;
}

/** A WhatsApp chat with the gym, the message already written. A bare 10-digit number is Indian. */
export function whatsappUrl(phone: string, message: string): string {
  const digits = phone.replace(/\D/g, '');
  const e164 = digits.length === 10 ? `91${digits}` : digits.replace(/^0+/, '');
  return `https://wa.me/${e164}?text=${encodeURIComponent(message)}`;
}

/**
 * Hand the member to the hosted pay page, telling it where to send them back.
 * The pay page honours only member-app paths, so this cannot be bent into a
 * redirect elsewhere.
 */
export function goToPay(url: string, back = '/app/bookings'): void {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('back', back);
  window.location.assign(target.toString());
}

/** My bookings: the button and the line under it on an unpaid online booking. */
export const payHeldCta = (amount: number) => `Pay ${inr(amount)}`;
export const payHeldLine = (clock: string) => `Spot held for ${clock} — pay to keep it`;
export const PAY_LAPSED_LINE = 'Time to pay ran out — your spot is being released';

/**
 * My bookings: the line on a drop-in the gym collects for that nobody has paid
 * yet (`amountDue`). Null when nothing is owed — paid at the desk, or covered.
 */
export function dueAtGymLine(amountDue: number | null | undefined): string | null {
  return amountDue && amountDue > 0 ? `${inr(amountDue)} due · pay at the gym` : null;
}

/**
 * Who may cancel this booking, and what goes with it. The server's policy
 * (api `services/commerce/bookingCancel.ts`, same rule as the mobile app's
 * lib/bookingPay.ts): a booking is cancelled whole, never one spot of it; the
 * member cancels while nothing is paid on it, within the cancellation window,
 * and once money is taken — online or at the desk — only the gym cancels it
 * (CANCEL_AT_GYM), arranging any refund by hand.
 *
 * An older backend sends neither `cancelBy` nor `spots`: then only an unpaid
 * multi-spot booking goes whole (GROUP_UNPAID) and the member cancels the rest.
 */
export function cancelPolicy(
  booking: {
    id: string;
    bookingGroupId?: string | null;
    pay?: unknown | null;
    amountDue?: number | null;
    cancelBy?: 'member' | 'gym';
    spots?: number;
  },
  rows: Array<{ id: string; status: string; bookingGroupId?: string | null }>
): { byGym: boolean; whole: boolean; spots: number } {
  if (booking.cancelBy) {
    const spots = Math.max(1, booking.spots ?? 1);
    return { byGym: booking.cancelBy === 'gym', whole: spots > 1, spots };
  }
  const unpaid = !!booking.pay || (booking.amountDue ?? 0) > 0;
  if (!unpaid || !booking.bookingGroupId) return { byGym: false, whole: false, spots: 1 };
  const spots = rows.filter(r => r.bookingGroupId === booking.bookingGroupId && r.status === 'booked').length;
  return spots > 1 ? { byGym: false, whole: true, spots } : { byGym: false, whole: false, spots: 1 };
}

/** Cancel codes that mean "these spots go together" — the server's current one and an older backend's. */
export const WHOLE_BOOKING_CODES = ['WHOLE_BOOKING', 'GROUP_UNPAID'];
